export function getRelativeTime(date: Date) {
  const now = new Date()
  const diffInSeconds = (now.getTime() - date.getTime()) / 1000

  let value: number, unit: Intl.RelativeTimeFormatUnit

  if (diffInSeconds < 60) {
    value = -Math.round(diffInSeconds)
    unit = 'second'
  } else if (diffInSeconds < 3600) {
    value = -Math.round(diffInSeconds / 60)
    unit = 'minute'
  } else if (diffInSeconds < 86_400) {
    value = -Math.round(diffInSeconds / 3600)
    unit = 'hour'
  } else {
    value = -Math.round(diffInSeconds / 86_400)
    return `${Math.abs(value)}d ago`
  }

  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' })
  const relativeTime = rtf.format(value, unit)

  return relativeTime
    .replace(/(\d+)\sseconds?/, '$1s')
    .replace(/(\d+)\sminutes?/, '$1m')
    .replace(/(\d+)\shours?/, '$1hr')
}

export interface CountdownParts {
  days: number
  hours: number
  minutes: number
  seconds: number
}

const defaultCountdownLabels = {
  day: 'd',
  hour: 'h',
  minute: 'm',
  second: 's',
}

type CountdownLabels = Partial<typeof defaultCountdownLabels>

export interface FormatCountdownOptions {
  separator?: string
  labels?: CountdownLabels
  compressDaysIntoMinutes?: boolean
  padHoursMinutesSeconds?: boolean
}

export function getCountdownPartsFromMs(diffMs: number): CountdownParts {
  if (!Number.isFinite(diffMs)) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0 }
  }

  const totalSeconds = Math.max(0, Math.floor(diffMs / 1000))
  const days = Math.floor(totalSeconds / 86_400)
  const hours = Math.floor((totalSeconds % 86_400) / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  return { days, hours, minutes, seconds }
}

export function formatCountdownParts(
  parts: CountdownParts,
  {
    separator = ' : ',
    labels = defaultCountdownLabels,
    compressDaysIntoMinutes = false,
    padHoursMinutesSeconds = true,
  }: FormatCountdownOptions = {},
) {
  const resolvedLabels = { ...defaultCountdownLabels, ...labels }
  const formatUnit = (value: number, label: string, shouldPad = false) =>
    `${shouldPad ? padTime(value) : value}${label}`

  if (compressDaysIntoMinutes) {
    const totalMinutes = parts.days * 1440 + parts.hours * 60 + parts.minutes
    return `${totalMinutes}${resolvedLabels.minute}${separator}${formatUnit(
      parts.seconds,
      resolvedLabels.second,
      padHoursMinutesSeconds,
    )}`
  }

  return [
    `${parts.days}${resolvedLabels.day}`,
    formatUnit(parts.hours, resolvedLabels.hour, padHoursMinutesSeconds),
    formatUnit(parts.minutes, resolvedLabels.minute, padHoursMinutesSeconds),
    formatUnit(parts.seconds, resolvedLabels.second, padHoursMinutesSeconds),
  ].join(separator)
}

export function formatCountdownFromMs(diffMs: number, options?: FormatCountdownOptions) {
  return formatCountdownParts(getCountdownPartsFromMs(diffMs), options)
}

function padTime(value: number) {
  return value.toString().padStart(2, '0')
}
